import { useEffect, useRef, useState } from 'react';

export type ImageSourceEncoding = 'object-url' | 'data-url';

const toDataUrl = async (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener('loadend', () => {
      resolve(reader.result as string);
    });
    reader.addEventListener('error', reject);
    reader.readAsDataURL(blob);
  });

/**
 * **Bytes an `<img>` can show, from wherever they come from.** Takes a loader rather than a url, so
 * a caller fetching one and a caller asking an api for one are the same shape here.
 *
 * `source` is `null` while there is nothing to show - before the bytes land, and for good if they
 * never do. **`isLoading` is what tells those two apart**, so a caller can hold a space open for a
 * picture that is coming without holding it open for one that is not. This reports no error of its
 * own: a caller with something to draw instead draws it.
 */
export const useImageSource = ({
  encode = 'object-url',
  imageKey,
  load,
}: {
  /**
   * `data-url` embeds the bytes in the markup, which is what a page being printed to PDF needs -
   * a `blob:` url does not survive that. It costs about a third more memory and cannot be revoked,
   * so it is the exception.
   */
  encode?: ImageSourceEncoding;
  /**
   * What identifies this image, and `null` for no image at all. The loader is held by a ref and
   * this is what the fetch is keyed on, so a loader written inline costs nothing.
   */
  imageKey: string | null;
  /**
   * **It may hand the bytes straight back.** A caller fetching them returns the promise; one that
   * already holds them - a file the reader just picked - returns the `Blob` itself, because
   * wrapping it in a resolved promise is a shape the lint rules have no spelling for.
   */
  load: () => Blob | Promise<Blob>;
}) => {
  const [source, setSource] = useState<string | null>(null);

  /*
    Which key has been answered for, rather than a loading flag: a flag can only be raised by the
    effect, which is a paint too late, and the first paint is the whole of the problem this solves.
  */
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const loadRef = useRef(load);

  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    setSource(null);

    if (!imageKey) return;

    /*
      A response that arrives after its key changed is not this image's. Without the latch a slow
      first request lands last and overwrites the one the reader is actually looking at.
    */
    let stale = false;
    let objectUrl: string | null = null;

    void Promise.resolve(loadRef.current())
      .then(async blob => {
        const minted =
          encode === 'data-url'
            ? await toDataUrl(blob)
            : URL.createObjectURL(blob);

        /*
          Checked after minting rather than before, because the decode is itself long enough for
          the key to move on - and what was minted in the meantime still has to be handed back.
        */
        if (stale) {
          if (encode !== 'data-url') URL.revokeObjectURL(minted);

          return;
        }

        if (encode !== 'data-url') objectUrl = minted;

        setSource(minted);
        setSettledKey(imageKey);
      })
      // A refusal settles this key too - there is no second attempt, so nothing is still coming.
      .catch(() => {
        if (!stale) setSettledKey(imageKey);
      });

    return () => {
      stale = true;

      // Only an object url is a handle the document holds; a data url is just a string.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [imageKey, encode]);

  return { isLoading: !!imageKey && settledKey !== imageKey, source };
};
